import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AlertTriangle, Upload } from 'lucide-react';
import { Button, EmptyState, StatusBadge, AdminPage } from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { supabase } from '@/lib/supabase';
import { errorMessage } from '@/lib/errors';
import { useEmployeeOptions } from '../api';
import { columnLabel, parseEmployeesCsv, resolveReferences, type ParsedRow } from './parse';

type Resolved = ParsedRow & {
  branch_id: string | null;
  job_title_id: string | null;
};

/** ייבוא עובדים מקובץ (PRD סעיף 4.1). */
export function ImportPage() {
  const canManage = usePerm('employees.manage');
  const { data: options } = useEmployeeOptions();

  const [fileName, setFileName] = useState('');
  const [raw, setRaw] = useState<ParsedRow[] | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [unknown, setUnknown] = useState<string[]>([]);
  const [existingPhones, setExistingPhones] = useState<Set<string>>(new Set());
  const [roleId, setRoleId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const rows: Resolved[] = useMemo(() => {
    if (!raw || !options) return [];
    return resolveReferences(raw, options.branches, options.jobTitles, existingPhones);
  }, [raw, options, existingPhones]);

  const valid = rows.filter((row) => row.errors.length === 0);
  const invalid = rows.filter((row) => row.errors.length > 0);

  if (!canManage) return <Navigate to="/admin" replace />;

  async function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setSaved(null);
    setFileName(file.name);

    if (/\.xlsx?$/i.test(file.name)) {
      setRaw(null);
      setError('הקובץ הוא Excel. כרגע נתמך CSV בלבד: פתח/י את הקובץ ושמור/י אותו בתור CSV UTF-8.');
      return;
    }

    const text = await file.text();
    const result = parseEmployeesCsv(text);
    setMissing(result.missingColumns.map(columnLabel));
    setUnknown(result.unknownHeaders);
    setRaw(result.rows);

    // בודקים מול הקיים, כדי לא לנסות להכניס טלפון שכבר יש
    const phones = result.rows.map((row) => row.phone).filter((p): p is string => p !== null);
    if (phones.length > 0) {
      const { data } = await supabase.from('employees').select('phone').in('phone', phones);
      setExistingPhones(new Set((data ?? []).map((row) => row.phone as string)));
    } else {
      setExistingPhones(new Set());
    }
  }

  async function save() {
    setError(null);
    setIsSaving(true);
    try {
      const payload = valid.map((row) => ({
        first_name: row.first_name,
        last_name: row.last_name,
        phone: row.phone!,
        email: row.email,
        birth_date: row.birth_date,
        start_date: row.start_date,
        primary_branch_id: row.branch_id,
        job_title_id: row.job_title_id,
        role_id: roleId,
        comax_employee_code: row.employee_code,
      }));

      const { error: failure } = await supabase.from('employees').insert(payload);
      if (failure) throw failure;

      setSaved(payload.length);
      setRaw(null);
      setFileName('');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <AdminPage
      backTo="/admin/employees"
      title="ייבוא עובדים"
      subtitle="מקובץ CSV, עם תצוגה מקדימה לפני השמירה"
    >
      <div className="grid gap-2 rounded-md border border-line p-4">
        <p className="m-0 text-body">
          הקובץ צריך לכלול לפחות <strong>שם פרטי</strong>, <strong>שם משפחה</strong> ו
          <strong> טלפון</strong>. אפשר להוסיף גם סניף, תפקיד, תאריך התחלה, תאריך לידה ומייל.
        </p>
        <p className="m-0 text-body-sm text-muted">
          שמות הסניפים והתפקידים בקובץ חייבים להתאים בדיוק לאלה שבמערכת. הטלפונים מנורמלים אוטומטית.
        </p>

        <label className="mt-2 inline-flex min-h-11 w-fit cursor-pointer items-center gap-2 border border-charcoal bg-charcoal px-6 font-display text-[20px] font-semibold text-on-charcoal">
          <Upload size={20} strokeWidth={1.75} aria-hidden="true" />
          בחירת קובץ
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="sr-only" />
        </label>
        {fileName && <p className="m-0 text-body-sm text-muted">{fileName}</p>}
      </div>

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {saved !== null && (
        <p role="status" className="m-0 text-body text-good">
          נשמרו {saved} עובדים. הם מופיעים עכשיו ברשימת העובדים.
        </p>
      )}

      {missing.length > 0 && (
        <div className="grid gap-1 rounded-md border border-bad p-4">
          <p className="m-0 flex items-center gap-2 text-body text-bad">
            <AlertTriangle size={20} strokeWidth={1.75} aria-hidden="true" />
            חסרות עמודות חובה: {missing.join(', ')}
          </p>
          <p className="m-0 text-body-sm text-muted">הוסיפו אותן לשורת הכותרת בקובץ ונסו שוב.</p>
        </div>
      )}

      {unknown.length > 0 && (
        <p className="m-0 text-body-sm text-muted">
          עמודות שלא זוהו ולא ייובאו: {unknown.join(', ')}
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge tone="good">{valid.length} תקינים</StatusBadge>
            {invalid.length > 0 && <StatusBadge tone="bad">{invalid.length} עם שגיאה</StatusBadge>}
          </div>

          <div className="grid gap-1">
            <label htmlFor="import-role" className="text-label font-medium">
              תפקיד הרשאות לכל המיובאים *
            </label>
            <select
              id="import-role"
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
              className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body"
            >
              <option value="">בחר/י</option>
              {(options?.roles ?? []).map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <p className="m-0 text-body-sm text-muted">אפשר לשנות לכל עובד בנפרד אחרי הייבוא.</p>
          </div>

          <ul className="grid list-none gap-2 p-0">
            {rows.map((row) => (
              <li
                key={row.line}
                className={`grid gap-1 rounded-md border p-3 ${
                  row.errors.length > 0 ? 'border-bad' : 'border-line'
                }`}
              >
                <span className="text-title font-medium">
                  {row.first_name} {row.last_name}
                </span>
                <span className="text-body-sm text-muted" dir="ltr">
                  {row.phone ?? row.rawPhone}
                </span>
                <span className="text-body-sm text-muted">
                  {[row.branch, row.job_title].filter(Boolean).join(' · ')}
                </span>
                {row.errors.length > 0 && (
                  <span className="text-body-sm text-bad">
                    שורה {row.line}: {row.errors.join('. ')}
                  </span>
                )}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              disabled={isSaving || valid.length === 0 || roleId === ''}
              onClick={() => void save()}
            >
              {isSaving ? 'שומר' : `ייבוא ${valid.length} עובדים`}
            </Button>
            {invalid.length > 0 && (
              <span className="text-body-sm text-muted">
                שורות עם שגיאה לא ייובאו. אפשר לתקן בקובץ ולטעון שוב.
              </span>
            )}
          </div>
        </>
      )}

      {raw !== null && rows.length === 0 && missing.length === 0 && (
        <EmptyState title="הקובץ ריק" description="אין בו שורות מלבד הכותרת." />
      )}
    </AdminPage>
  );
}
